import React, { useState, useCallback } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { useNavigate } from 'react-router-dom';
import { Shield, Users, Database, Key, Building2, Lock } from 'lucide-react';
import DashboardLayout from '../../components/common/DashboardLayout';
import DashboardTile from '../../components/common/DashboardTile';
import { PieTooltip, BarTooltip } from '../../components/common/ChartTooltips';
import { adminManagementApi } from '../../services/api';

const ROLE_COLORS = {
  SysAdmin: '#dc2626',
  Admin: '#ea580c',
  AppAdmin: '#ea580c',
  PowerUser: '#2563eb',
  User: '#059669',
  Guest: '#94a3b8'
};

const SecurityDashboardPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [summaryRes, usersRes, dbRes] = await Promise.all([
        adminManagementApi.getSummary(),
        adminManagementApi.getUsers(),
        adminManagementApi.getDatabases()
      ]);

      const users = usersRes.data;
      const summary = summaryRes.data;
      const databases = dbRes.data;

      // System role distribution
      const roleCounts = {};
      users.forEach(u => {
        const r = u.role || 'Guest';
        roleCounts[r] = (roleCounts[r] || 0) + 1;
      });
      const roleDistribution = Object.entries(roleCounts)
        .map(([name, value]) => ({ name, value }))
        .sort((a, b) => b.value - a.value);

      // Users per database
      const dbUserCounts = databases.map(db => {
        const count = users.filter(u => u.databaseRoles?.some(dr => dr.databaseId === db.id)).length;
        return { name: db.displayName, users: count };
      });

      // DB role distribution (across all user-db assignments)
      const dbRoleCounts = {};
      users.forEach(u => {
        (u.databaseRoles || []).forEach(dr => {
          dbRoleCounts[dr.role] = (dbRoleCounts[dr.role] || 0) + 1;
        });
      });
      const dbRoleDistribution = Object.entries(dbRoleCounts)
        .map(([name, value]) => ({ name, value }))
        .sort((a, b) => b.value - a.value);

      // Active vs inactive
      const activeInactive = [
        { name: 'Active', value: summary.activeUsers },
        { name: 'Inactive', value: summary.users - summary.activeUsers }
      ].filter(d => d.value > 0);

      setData({
        summary,
        roleDistribution,
        dbUserCounts,
        dbRoleDistribution,
        activeInactive,
        usersWithRoles: users.filter(u => u.databaseRoles?.length > 0).length,
        usersWithoutRoles: users.filter(u => !u.databaseRoles?.length).length
      });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load security data');
    } finally {
      setLoading(false);
    }
  }, []);

  const StatCard = ({ value, label, icon: Icon, color = 'var(--color-primary)' }) => (
    <div className="stat-card">
      <div className="stat-icon" style={{ color }}><Icon size={24} /></div>
      <div className="stat-value">{value}</div>
      <div className="stat-label">{label}</div>
    </div>
  );

  return (
    <DashboardLayout
      id="security"
      title="Security"
      icon={Shield}
      onRefresh={loadData}
      loading={loading}
      error={error}
    >
      {data && (
        <>
          {/* Row 1: Stats */}
          <DashboardTile title="Total Users" icon={Users} onClick={() => navigate('/admin/users')} colSpan={1}>
            <StatCard value={data.summary.users} label="users" icon={Users} />
          </DashboardTile>

          <DashboardTile title="Active Users" icon={Users} colSpan={1}>
            <StatCard value={data.summary.activeUsers} label="active" icon={Users} color="var(--color-success)" />
          </DashboardTile>

          <DashboardTile title="Instances" icon={Database} onClick={() => navigate('/admin/databases')} colSpan={1}>
            <StatCard value={data.summary.databases} label="databases" icon={Database} color="var(--color-primary)" />
          </DashboardTile>

          <DashboardTile title="Permissions" icon={Key} onClick={() => navigate('/admin/permissions')} colSpan={1}>
            <StatCard value={data.summary.permissions} label="defined" icon={Key} color="#8b5cf6" />
          </DashboardTile>

          <DashboardTile title="Organizations" icon={Building2} onClick={() => navigate('/admin/organizations')} colSpan={1}>
            <StatCard value={data.summary.organizations} label="orgs" icon={Building2} color="var(--color-warning)" />
          </DashboardTile>

          <DashboardTile title="Role Defaults" icon={Lock} onClick={() => navigate('/admin/roles')} colSpan={1}>
            <StatCard value={data.summary.defRoleEntries} label="entries" icon={Lock} color="#2563eb" />
          </DashboardTile>

          {/* Row 2: Charts */}
          <DashboardTile title="System Roles" icon={Users} colSpan={2}>
            <ResponsiveContainer width="100%" height={160}>
              <PieChart>
                <Pie data={data.roleDistribution} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={60} innerRadius={30}>
                  {data.roleDistribution.map((entry, i) => (
                    <Cell key={i} fill={ROLE_COLORS[entry.name] || '#94a3b8'} />
                  ))}
                </Pie>
                <Tooltip content={<PieTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </DashboardTile>

          <DashboardTile title="Instance Roles" icon={Shield} colSpan={2}>
            <ResponsiveContainer width="100%" height={160}>
              <PieChart>
                <Pie data={data.dbRoleDistribution} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={60} innerRadius={30}>
                  {data.dbRoleDistribution.map((entry, i) => (
                    <Cell key={i} fill={ROLE_COLORS[entry.name] || '#94a3b8'} />
                  ))}
                </Pie>
                <Tooltip content={<PieTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </DashboardTile>

          <DashboardTile title="Users per Instance" icon={Database} colSpan={2}>
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={data.dbUserCounts} layout="vertical" margin={{ left: 10, right: 20, top: 5, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" allowDecimals={false} />
                <YAxis type="category" dataKey="name" width={40} tick={{ fontSize: 12 }} />
                <Tooltip content={<BarTooltip />} />
                <Bar dataKey="users" fill="var(--color-primary)" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </DashboardTile>

          {/* Row 3: Coverage */}
          <DashboardTile title="User Status" icon={Users} colSpan={2}>
            <ResponsiveContainer width="100%" height={160}>
              <PieChart>
                <Pie data={data.activeInactive} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={60} innerRadius={30}>
                  <Cell fill="#059669" />
                  {data.activeInactive.length > 1 && <Cell fill="#ef4444" />}
                </Pie>
                <Tooltip content={<PieTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </DashboardTile>

          <DashboardTile title="Role Assignment" icon={Shield} colSpan={2}
            legend={[
              { color: 'var(--color-success)', label: 'With Roles', value: data.usersWithRoles },
              { color: 'var(--color-gray-400)', label: 'No Roles', value: data.usersWithoutRoles }
            ]}
          >
            <ResponsiveContainer width="100%" height={160}>
              <PieChart>
                <Pie
                  data={[
                    { name: 'With Roles', value: data.usersWithRoles },
                    { name: 'No Roles', value: data.usersWithoutRoles }
                  ]}
                  dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={60} innerRadius={30}
                >
                  <Cell fill="var(--color-success)" />
                  <Cell fill="var(--color-gray-400)" />
                </Pie>
                <Tooltip content={<PieTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </DashboardTile>

          <DashboardTile title="Overrides" icon={Key} colSpan={2}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '160px' }}>
              <div style={{ fontSize: 'var(--text-3xl)', fontWeight: 700, color: 'var(--color-warning)' }}>{data.summary.userOverrides}</div>
              <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-500)' }}>user permission overrides</div>
              <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-400)', marginTop: 'var(--spacing-sm)' }}>across {data.summary.userRoles} instance roles</div>
            </div>
          </DashboardTile>
        </>
      )}
    </DashboardLayout>
  );
};

export default SecurityDashboardPage;
